import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Stack, Typography } from '@mui/material';
import { transactionsWithIncludes } from '../../store/slices/transactions';
import useIsMobile from '../../hooks/useIsMobile';
import { ClosingStatus } from '@prisma/client';
import {
  calculateDaysToClose,
  getTransactionClosingInfo,
} from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { usePropertyTimezone } from '../../hooks/usePropertyTimezone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  transaction: transactionsWithIncludes;
}

const ClosingDate: React.FC<Props> = ({ transaction }) => {
  const isMobile = useIsMobile();
  const { currentUser } = useAuth();
  const userTimezone = currentUser?.timezone || 'America/Chicago';
  // Coordinate-resolved, so a straddle state like Tennessee doesn't collapse
  // to the state map's single (wrong) Central zone for an Eastern-TN home.
  const propertyTimezone = usePropertyTimezone(transaction.listings);

  // Use the new utilities to get the closing date info
  const closingInfo = getTransactionClosingInfo(
    transaction,
    userTimezone,
    propertyTimezone
  );

  // Early return if no closing date exists
  if (!closingInfo.date || !closingInfo.dateOnlyString) {
    return null;
  }

  const daysDifference = calculateDaysToClose(closingInfo.date);

  // A passed closing date is not itself a closing — the deal could be
  // delayed, fell through, or is just waiting on someone to mark it closed.
  // Only `transaction.closing === COMPLETED` means the transaction actually
  // closed; a passed date with no completion reads as overdue, not closed.
  const isClosed = transaction?.closing === ClosingStatus.COMPLETED;
  const isOverdue = !isClosed && daysDifference < 0;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {isClosed ? (
        <ClosedDisplay
          date={closingInfo.dateOnlyString}
          time={closingInfo.timeString}
        />
      ) : isOverdue ? (
        <OverdueDisplay
          date={closingInfo.dateOnlyString}
          time={closingInfo.timeString}
        />
      ) : (
        <UpcomingClosingDisplay
          daysToClose={daysDifference}
          date={closingInfo.dateOnlyString}
          time={closingInfo.timeString}
          isMobile={isMobile}
        />
      )}
    </Stack>
  );
};

// Subcomponents for different display states
const UpcomingClosingDisplay = ({
  daysToClose,
  date,
  time,
  isMobile,
}: {
  daysToClose: number;
  date: string;
  time: string | null;
  isMobile: boolean;
}) => (
  <Stack direction={isMobile ? 'column' : 'row'} alignItems={'center'}>
    <Typography variant="body1" textAlign={'center'}>
      <b>{daysToClose} days until closing</b> {isMobile ? <br /> : 'on'} {date}
      {time ? ` at ${time}` : ' · time to be confirmed'}
    </Typography>
  </Stack>
);

const ClosedDisplay = ({
  date,
  time,
}: {
  date: string;
  time: string | null;
}) => (
  <Stack direction="column" alignItems="center">
    <Typography variant="h6">Closed on</Typography>
    <Typography variant="subtitle2">
      {date}
      {time && <> at {time}</>}
    </Typography>
  </Stack>
);

const OverdueDisplay = ({
  date,
  time,
}: {
  date: string;
  time: string | null;
}) => (
  <Stack direction="column" alignItems="center">
    <Typography variant="h6" color="warning.main">
      Closing overdue
    </Typography>
    <Typography variant="subtitle2">
      Was {date}
      {time && <> at {time}</>}
    </Typography>
  </Stack>
);

export default ClosingDate;
